import { MouseEvent, useEffect, useState } from 'react';
import { ActionModal, Input, Dropdown } from 'components/ProfileUIComponents';
import { Text, Flexbox } from '@morressier/ts';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { KeyedMutator } from 'swr';
import {
  faTrash,
  faSquarePlus,
  faCircleInfo
} from '@fortawesome/pro-regular-svg-icons';
import { useForm, useFieldArray, Controller } from 'react-hook-form';
import {
  Label,
  LinkButton,
  ProfileHeading,
  FieldContainer,
  StyledRadio,
  PronounsInput,
  LinkInputContainer,
  PrimaryAffiliationInput,
  Italic
} from './style';
import { Tooltip } from '../styles';
import { linkOrcid, revokeOrcidID } from 'clients/internal/authGateway';

import { updateProfile } from 'clients/internal/profileService';
import { countriesOptions } from '../countriesList';
import { IProfile } from 'typings/profile';
import { IPrimaryAffilation } from 'typings/profile';
import ProfilePhotoSection from './ProfilePhotoSection';
import { Button } from 'components/ProfileUIComponents';
import {
  faLinkSimple,
  faLinkSimpleSlash
} from '@fortawesome/pro-regular-svg-icons';
import { faExclamationSquare } from '@fortawesome/pro-solid-svg-icons';
import { useAuthContext } from 'context/AuthProvider';
import { logEvent } from 'utils/eventTracking';
import _isArray from 'lodash/isArray';
import {
  generalInfoFields,
  handleGeneralInfoTracking
} from 'utils/eventTracking/events';

interface IProfileFormModalProps {
  isOpen: boolean;
  onDismiss: () => void;
  data: FormValues;
  mutateProfile: KeyedMutator<IProfile>;
}
interface FormValues {
  title?: string;
  firstName: string;
  lastName: string;
  pronouns?: string;
  customPronouns?: string;
  headline?: string;
  countryAlpha2: string;
  linkedin?: string;
  twitter?: string;
  profilePictureURL?: string;
  websiteLinks?: { title: string; url: string }[];
  primaryAffiliation?: IPrimaryAffilation;
  orcid?: string;
  orcidLink?: boolean;
}
type FormValuesKeys =
  | 'title'
  | 'firstName'
  | 'lastName'
  | 'pronouns'
  | 'websiteLinks'
  | 'headline'
  | 'countryAlpha2'
  | 'linkedin'
  | 'twitter'
  | 'profilePictureURL'
  | 'orcid'
  | 'orcidLink';

const ProfileFormModal = (props: IProfileFormModalProps) => {
  const { isOpen, onDismiss, data: defaultValues, mutateProfile } = props;

  const { primaryAffiliation } = defaultValues;

  const [profilePhotoUrl, setProfilePhotoUrl] = useState(
    defaultValues?.profilePictureURL
  );
  const [showExitModal, setShowExitModal] = useState<boolean>(false);
  const closeExitModal = () => setShowExitModal(false);

  const openExitModal = () => setShowExitModal(true);

  const userData = useAuthContext();
  const userId = userData?.id;
  const email = userData?.email;

  const {
    register,
    control,
    handleSubmit,
    watch,
    // isDirty Set to true after the user modifies any of the inputs.
    formState: { errors, isDirty },
    setValue,
    reset
  } = useForm<FormValues>();
  const { fields, remove, append } = useFieldArray({
    control,
    name: 'websiteLinks'
  });

  const onAdd = (e: MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    append({ url: '', title: '' });
  };
  const onRemove = (index: number) => () => {
    remove(index);
  };

  const revokeOrcidFromProfile = async () => {
    await revokeOrcidID(userId!);
    mutateProfile();
  };

  const [unlinkOrcid, setUnlinkOrcid] = useState(false);

  const handleUnlinkOrcid = () => {
    setUnlinkOrcid(true);
    logEvent('PROFILE_GENERAL_INFORMATION_UNLINK_ORCID_PROFILE_CLICKED');
    setValue('orcidLink', false, { shouldDirty: true });
  };

  const handleLinkOrcid = async (e: MouseEvent<HTMLButtonElement>) => {
    logEvent('PROFILE_GENERAL_INFORMATION_LINK_ORCID_PROFILE_CLICKED');
    e.preventDefault();
    setValue('orcid', '', { shouldDirty: true });
    setValue('orcidLink', true, { shouldDirty: true });

    await linkOrcid(userId!, email, orcidResponse => {
      mutateProfile();
      logEvent(
        orcidResponse
          ? 'PROFILE_GENERAL_INFORMATION_LINK_ORCID_PROFILE_AUTH_SUCCESSFUL'
          : 'PROFILE_GENERAL_INFORMATION_LINK_ORCID_PROFILE_AUTH_UNSUCCESSFUL'
      );
      return;
    });
  };

  const onUpdate = async (data: FormValues) => {
    try {
      logEvent('PROFILE_GENERAL_INFORMATION_EDIT_UPDATE_PROFILE_CLICKED');
      const { customPronouns, ...formData } = data;
      if (customPronouns && data.pronouns === 'custom') {
        formData.pronouns = customPronouns;
      }
      const keys = Object.keys(formData) as FormValuesKeys[];
      formData.profilePictureURL = profilePhotoUrl;
      const formKeys = Object.keys(formData);
      keys.forEach((key: FormValuesKeys) => {
        if (formData[key] === '' || formData[key] === undefined) {
          delete formData[key];
        }
      });

      if (!unlinkOrcid) {
        formData.orcid = defaultValues?.orcid;
      }

      if (unlinkOrcid) {
        formData.orcid = undefined;
        revokeOrcidFromProfile();
        setUnlinkOrcid(false);
      }

      await updateProfile(userId!, formData);

      formData.profilePictureURL = formData.profilePictureURL || '';

      formKeys
        .filter((field: string) => generalInfoFields.indexOf(field) !== -1)
        .map((field: string) => {
          handleGeneralInfoTracking(
            field,
            (defaultValues as any)[field] || null,
            (formData as any)[field]
          );
        });

      // TODO: simplify the flow of orcid link and unlink
      // we are using 3 variables and the information in them is not in sync
      if (defaultValues.orcid && !formData.orcid)
        logEvent('PROFILE_GENERAL_INFORMATION_LINK_ORCID_PROFILE_REMOVED');
      else if (
        (!defaultValues.orcid || !formData.orcid) &&
        !defaultValues.orcidLink &&
        formData.orcidLink
      )
        logEvent('PROFILE_GENERAL_INFORMATION_LINK_ORCID_PROFILE_ADDED');

      mutateProfile();

      if (data.profilePictureURL !== defaultValues.profilePictureURL) {
        userData?.mutate(
          { ...userData, picture_url: data.profilePictureURL || '' },
          { revalidate: false }
        );
      }
      reset(data);
      onDismiss();
    } catch (err) {}
  };

  const resetEdit = () => {
    closeExitModal();
    reset();
    setProfilePhotoUrl(defaultValues?.profilePictureURL || '');
    onDismiss();
    setUnlinkOrcid(false);
  };
  const onExit = (clickedExit = false) => {
    if (clickedExit)
      logEvent('PROFILE_GENERAL_INFORMATION_EDIT_EXIT_WITHOUT_SAVING_CLICKED');
    else logEvent('PROFILE_GENERAL_INFORMATION_CLICKED_OUTSIDE_MODAL');
    if (isDirty) {
      openExitModal();
    } else resetEdit();
  };
  const updateProfilePhoto = (photo: string) => {
    // Set dirty flag if profilePictureURL changes
    setValue('profilePictureURL', photo, { shouldDirty: true });
    setProfilePhotoUrl(photo);
  };

  useEffect(() => {
    if (!defaultValues.pronouns) defaultValues.pronouns = 'no-preference';
    if (
      !['no-preference', 'They/Them', 'She/Her', 'He/Him'].includes(
        defaultValues.pronouns || ''
      )
    ) {
      defaultValues.customPronouns = defaultValues.pronouns;
      defaultValues.pronouns = 'custom';
    }

    if (!['doctor', 'professor'].includes(defaultValues.title || ''))
      defaultValues.title = 'none';
    reset(defaultValues);
  }, []);

  return (
    <ActionModal
      title="Update your profile"
      isOpen={isOpen}
      onDismiss={onExit}
      onConfirmExitAction={() => {
        logEvent(
          'PROFILE_GENERAL_INFORMATION_EDIT_EXIT_WITHOUT_SAVING_CONFIRM_CLICKED'
        );
        resetEdit();
      }}
      onCancelExitAction={() => {
        logEvent(
          'PROFILE_GENERAL_INFORMATION_EDIT_EXIT_WITHOUT_SAVING_BACK_CLICKED'
        );
        closeExitModal();
      }}
      showExitContainer={showExitModal}
      cautionaryAction={{
        title: 'Exit without saving',
        onClick: () => onExit()
      }}
      primaryAction={{
        title: 'Update profile',
        onClick: handleSubmit(onUpdate)
      }}
    >
      <form>
        <ProfilePhotoSection
          userId={userId!}
          profilePhotoUrl={profilePhotoUrl}
          setProfilePhoto={updateProfilePhoto}
        />
        <FieldContainer>
          <Label>
            <Text size="body1_new" fontWeight="bold">
              Title
            </Text>
          </Label>
          <Flexbox>
            <StyledRadio value="doctor" {...register('title')}>
              Doctor
            </StyledRadio>
            <StyledRadio value="professor" {...register('title')}>
              Professor
            </StyledRadio>
            <StyledRadio value="none" {...register('title')}>
              None
            </StyledRadio>
          </Flexbox>
        </FieldContainer>
        <Flexbox>
          <FieldContainer style={{ width: '50%', marginRight: '1rem' }}>
            <Label>
              <Text size="body1_new" fontWeight="bold">
                First name
              </Text>
            </Label>
            <Controller
              name="firstName"
              rules={{
                required: 'First name is required',
                pattern: {
                  value: /^.{0,100}$/,
                  message: 'First name should not exceed 100 characters'
                }
              }}
              control={control}
              render={({ field }) => (
                <Input
                  placeholder="First name"
                  error={errors.firstName?.message}
                  {...field}
                />
              )}
            />
          </FieldContainer>
          <FieldContainer style={{ width: '50%', marginRight: '1rem' }}>
            <Label>
              <Text size="body1_new" fontWeight="bold">
                Last name
              </Text>
            </Label>
            <Controller
              name="lastName"
              rules={{
                required: 'Last name is required',
                pattern: {
                  value: /^.{0,100}$/,
                  message: 'Last name should not exceed 100 characters'
                }
              }}
              control={control}
              render={({ field }) => (
                <Input
                  placeholder="Last name"
                  error={errors.lastName?.message}
                  {...field}
                />
              )}
            />
          </FieldContainer>
        </Flexbox>
        <FieldContainer>
          <Label>
            <Text size="body1_new" fontWeight="bold">
              Your preferred pronouns
            </Text>
            <Text size="body1_new">Let others know how to refer to you.</Text>
          </Label>
          <Flexbox alignItems="center" style={{ height: 40 }}>
            <StyledRadio {...register('pronouns')} value="no-preference">
              No preference
            </StyledRadio>
            <StyledRadio {...register('pronouns')} value="They/Them">
              They/Them
            </StyledRadio>
            <StyledRadio {...register('pronouns')} value="She/Her">
              She/Her
            </StyledRadio>
            <StyledRadio {...register('pronouns')} value="He/Him">
              He/Him
            </StyledRadio>
            <StyledRadio {...register('pronouns')} value="custom">
              Add your own
            </StyledRadio>
            {watch('pronouns') === 'custom' && (
              <div>
                <Controller
                  control={control}
                  name="customPronouns"
                  rules={{
                    required: 'Please enter custom pronoun'
                  }}
                  render={({ field }) => (
                    <PronounsInput
                      {...field}
                      error={errors.customPronouns?.message}
                    />
                  )}
                />
              </div>
            )}
          </Flexbox>
        </FieldContainer>
        <FieldContainer>
          <Label>
            <Text size="body1_new" fontWeight="bold">
              Headline
            </Text>
            <Text size="body1_new">Introduce yourself to profile visitors</Text>
          </Label>
          <Controller
            name="headline"
            rules={{
              pattern: {
                value: /^.{0,220}$/,
                message: 'Headline should not exceed 220 characters'
              }
            }}
            control={control}
            render={({ field }) => (
              <Input
                placeholder="Headline"
                {...field}
                error={errors.headline?.message}
                limit={220}
              />
            )}
          />
        </FieldContainer>
        <FieldContainer>
          <Label>
            <Text size="body1_new" fontWeight="bold">
              Country
            </Text>
          </Label>
          <Controller
            name="countryAlpha2"
            control={control}
            rules={{ required: 'Country is required' }}
            render={({ field }) => (
              <Dropdown
                options={countriesOptions}
                isSearchable
                placeholder="Select Country"
                error={errors.countryAlpha2?.message}
                {...field}
              />
            )}
          />
        </FieldContainer>
        <FieldContainer>
          <ProfileHeading>Professional Profiles</ProfileHeading>
          <Label>
            <Text size="body1_new" fontWeight="semiBold">
              ORCID
            </Text>
          </Label>
          {Boolean(defaultValues?.orcid) ? (
            <Flexbox justifyContent="space-between" alignItems="center">
              {!unlinkOrcid && (
                <LinkInputContainer>
                  <Input value={`${defaultValues.orcid}`} disabled />
                </LinkInputContainer>
              )}
              <Flexbox justifyContent="space-between" alignItems="center">
                {unlinkOrcid && (
                  <Flexbox
                    gap={'4px'}
                    alignItems="center"
                    style={{ marginBottom: '0.5rem' }}
                  >
                    <FontAwesomeIcon
                      icon={faExclamationSquare}
                      color="var(--color-red-50)"
                    />
                    <Text size="body1_new" color="red">
                      Update your profile to save this change
                    </Text>
                  </Flexbox>
                )}
                {!unlinkOrcid && (
                  <Button
                    style={{ marginBottom: '1rem' }}
                    color="error"
                    onClick={() => handleUnlinkOrcid()}
                    iconBefore={<FontAwesomeIcon icon={faLinkSimpleSlash} />}
                    type="button"
                  >
                    Unlink
                  </Button>
                )}
              </Flexbox>
            </Flexbox>
          ) : (
            <LinkInputContainer>
              <Button
                color="secondary"
                onClick={handleLinkOrcid}
                iconBefore={<FontAwesomeIcon icon={faLinkSimple} />}
              >
                Link ORCID Profile
              </Button>
            </LinkInputContainer>
          )}
          <Label>
            <Text size="body1_new" fontWeight="semiBold">
              Linkedin
            </Text>
          </Label>
          <LinkInputContainer>
            <Controller
              name="linkedin"
              rules={{
                pattern: {
                  value:
                    /^https?:\/\/((www|[a-z][a-z])\.)?linkedin.com\/((in\/[^/]+\/?)|(pub\/[^/]+\/((\w|\d)+\/?){3}))$/,
                  message: 'Linkedin profile url is not valid'
                }
              }}
              control={control}
              render={({ field }) => (
                <Input
                  placeholder="e.g. https://www.linkedin.com/in/example"
                  {...field}
                  error={errors.linkedin?.message}
                />
              )}
            />
          </LinkInputContainer>
          <Label>
            <Text size="body1_new" fontWeight="semiBold">
              Twitter
            </Text>
          </Label>
          <LinkInputContainer>
            <Controller
              name="twitter"
              rules={{
                pattern: {
                  value:
                    /^http(?:s)?:\/\/(?:www\.)?twitter\.com\/([a-zA-Z0-9_]+)$/,
                  message: 'Twitter profile url is not valid'
                }
              }}
              control={control}
              render={({ field }) => (
                <Input
                  placeholder="e.g. https://www.twitter.com/example"
                  {...field}
                  error={errors.twitter?.message}
                />
              )}
            />
          </LinkInputContainer>
        </FieldContainer>
        <FieldContainer>
          <Label>
            <Text size="body1_new" fontWeight="bold">
              Websites
            </Text>
          </Label>
          {fields.map((field, index) => (
            <div key={field.id}>
              <Label>
                <Text size="body1_new">Url</Text>
              </Label>
              <LinkInputContainer>
                <Controller
                  name={`websiteLinks.${index}.url`}
                  rules={{
                    required: 'Url is required',
                    pattern: {
                      value:
                        /^(?:https?:\/\/)[a-z0-9]+([\-\.]{1}[a-z0-9]+)*\.[a-z]{2,5}(:[0-9]{1,5})?(\/.*)?$/,
                      message: 'Link is not a valid url'
                    }
                  }}
                  control={control}
                  render={({ field }) => (
                    <Input
                      placeholder="e.g. https://www.website.com"
                      {...field}
                      error={errors.websiteLinks?.[index]?.url?.message}
                    />
                  )}
                />
              </LinkInputContainer>
              <Label>
                <Text size="body1_new">Title</Text>
              </Label>
              <Controller
                name={`websiteLinks.${index}.title`}
                rules={{
                  required: 'Title is required',
                  pattern: {
                    value: /^.{0,20}$/,
                    message: "Title shouldn't exceed 20 characters"
                  }
                }}
                control={control}
                render={({ field }) => (
                  <Input
                    placeholder="e.g. My website"
                    {...field}
                    limit={20}
                    error={errors.websiteLinks?.[index]?.title?.message}
                  />
                )}
              />
              <LinkButton btnType="remove" onClick={onRemove(index)}>
                <FontAwesomeIcon icon={faTrash} />
                Remove
              </LinkButton>
            </div>
          ))}
          {fields.length < 3 && (
            <LinkButton btnType="add" onClick={onAdd}>
              <FontAwesomeIcon icon={faSquarePlus} />
              Add Website
            </LinkButton>
          )}
        </FieldContainer>
      </form>
    </ActionModal>
  );
};
export default ProfileFormModal;
